import { Controller } from "@hotwired/stimulus"
import { select, pointer } from "d3-selection"
import { geoMercator, geoPath } from "d3-geo"
import { feature } from "topojson-client"
import { MAP_WIDTH, MAP_HEIGHT, MAINLAND_CENTER, MAINLAND_SCALE, OKINAWA_CENTER } from "../lib/japan_map_projections"

const OKINAWA_ID = 47

// 全国表示用プロジェクション（沖縄〜北海道）
const FULL_CENTER = [136, 35.5]
const FULL_SCALE = 1500

// 沖縄インセットの設定（左）
const INSET_MARGIN = 50
const INSET_WIDTH = 120
const INSET_HEIGHT = 120
const OKINAWA_SCALE = 2000

// モジュールスコープのキャッシュ（Turbo遷移間で保持される）
let geoDataPromise = null
let geoJsonCache = null

export default class extends Controller {
  static targets = ["container", "loading"]
  static values = {
    prefectures: { type: Object, default: {} },
    defaultColor: { type: String, default: "#D1D5DB" },
    urlPattern: { type: String, default: "" },
    okinawaInset: { type: Boolean, default: false },
    seaColor: { type: String, default: "" }
  }

  connect() {
    // SVG clipPath ID はドキュメント全体でユニークである必要があるため、インスタンスごとに接尾辞を生成
    this._clipId = Math.random().toString(36).slice(2, 8)
    this._disconnected = false
    this.initMap()
  }

  disconnect() {
    this._disconnected = true
    if (this.hasContainerTarget) {
      this.containerTarget.innerHTML = ""
    }
    this.tooltip = null
  }

  async initMap() {
    this.prefetchGeoData()
    try {
      await this.renderMap()
    } catch (error) {
      if (this._disconnected) return
      console.error("Failed to load map:", error)
      this.loadingTarget.textContent = "地図の読み込みに失敗しました"
    }
  }

  prefetchGeoData() {
    if (!geoDataPromise) {
      geoDataPromise = fetch("/maps/japan-prefectures.topojson")
        .then(response => {
          if (!response.ok) throw new Error(`TopoJSON fetch failed: ${response.status}`)
          return response.json()
        })
        .catch(error => {
          geoDataPromise = null
          throw error
        })
    }
  }

  async renderMap() {
    const geojson = await this.loadGeoJson()
    if (this._disconnected) return

    if (this.okinawaInsetValue) {
      this.renderWithInset(geojson)
    } else {
      this.renderFull(geojson)
    }

    this.showMap()
  }

  renderFull(geojson) {
    const clipId = `main-clip-${this._clipId}`
    const { svg, path } = this.createSvg(FULL_CENTER, FULL_SCALE)
    this.addClipPath(svg, clipId, 0, 0, MAP_WIDTH, MAP_HEIGHT)
    this.drawPrefectures(svg.append("g").attr("clip-path", `url(#${clipId})`), path, geojson.features)
  }

  renderWithInset(geojson) {
    const mainland = geojson.features.filter(f => f.properties.id !== OKINAWA_ID)
    const okinawa = geojson.features.filter(f => f.properties.id === OKINAWA_ID)

    const clipId = `main-clip-${this._clipId}`
    const { svg, path } = this.createSvg(MAINLAND_CENTER, MAINLAND_SCALE)
    this.addClipPath(svg, clipId, 0, 0, MAP_WIDTH, MAP_HEIGHT)
    this.drawPrefectures(svg.append("g").attr("clip-path", `url(#${clipId})`), path, mainland)

    this.drawInset(svg, okinawa)
  }

  drawInset(svg, okinawaFeatures) {
    const insetX = INSET_MARGIN
    const insetY = MAP_HEIGHT * 0.2

    const insetGroup = svg.append("g")

    // インセット背景と枠線（海の色を全体と統一）
    const insetBg = this.seaColorValue || "#fff"
    insetGroup.append("rect")
      .attr("x", insetX)
      .attr("y", insetY)
      .attr("width", INSET_WIDTH)
      .attr("height", INSET_HEIGHT)
      .attr("fill", insetBg)
      .attr("stroke", "#D1D5DB")
      .attr("stroke-width", "0.5")
      .attr("rx", "3")

    // 沖縄用プロジェクション
    const insetProjection = geoMercator()
      .center(OKINAWA_CENTER)
      .scale(OKINAWA_SCALE)
      .translate([insetX + INSET_WIDTH / 2, insetY + INSET_HEIGHT / 2])

    const insetPath = geoPath().projection(insetProjection)

    const insetClipId = `inset-clip-${this._clipId}`
    this.addClipPath(svg, insetClipId, insetX, insetY, INSET_WIDTH, INSET_HEIGHT, "3")
    this.drawPrefectures(insetGroup.append("g").attr("clip-path", `url(#${insetClipId})`), insetPath, okinawaFeatures)

    // 「沖縄」ラベル
    insetGroup.append("text")
      .attr("x", insetX + INSET_WIDTH / 2)
      .attr("y", insetY + INSET_HEIGHT - 6)
      .attr("text-anchor", "middle")
      .attr("fill", "#6B7280")
      .attr("font-size", "10")
  }

  addClipPath(svg, id, x, y, width, height, rx = "0") {
    let defs = svg.select("defs")
    if (defs.empty()) defs = svg.append("defs")

    defs.append("clipPath")
      .attr("id", id)
      .append("rect")
      .attr("x", x)
      .attr("y", y)
      .attr("width", width)
      .attr("height", height)
      .attr("rx", rx)
  }

  async loadGeoJson() {
    if (!geoJsonCache) {
      const topoData = await geoDataPromise
      geoJsonCache = feature(topoData, topoData.objects.japan)
    }
    return geoJsonCache
  }

  createSvg(center, scale) {
    const projection = geoMercator()
      .center(center)
      .scale(scale)
      .translate([MAP_WIDTH / 2, MAP_HEIGHT / 2])

    const path = geoPath().projection(projection)

    const svg = select(this.containerTarget)
      .append("svg")
      .attr("viewBox", `0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`)
      .attr("class", "w-full h-auto")

    if (this.seaColorValue) {
      svg.append("rect")
        .attr("width", MAP_WIDTH)
        .attr("height", MAP_HEIGHT)
        .attr("fill", this.seaColorValue)
    }

    return { svg, path }
  }

  drawPrefectures(parent, path, features) {
    const prefectures = this.prefecturesValue
    const defaultColor = this.defaultColorValue

    if (!this.tooltip) {
      this.tooltip = select(this.containerTarget)
        .append("div")
        .attr("class", "absolute hidden bg-gray-800 text-white text-xs rounded px-2 py-1 pointer-events-none z-10")
    }
    const tooltip = this.tooltip

    parent.selectAll(null)
      .data(features)
      .enter()
      .append("a")
      .attr("href", d => this.buildUrl(d.properties))
      .append("path")
      .attr("d", path)
      .attr("fill", d => {
        const code = this.prefectureCode(d.properties)
        return prefectures[code]?.color || defaultColor
      })
      .attr("stroke", "#fff")
      .attr("stroke-width", "0.5")
      .style("cursor", "pointer")
      .style("transition", "opacity 0.15s")
      .on("mouseenter", function() { select(this).attr("opacity", 0.7) })
      .on("mouseleave", function() {
        select(this).attr("opacity", 1)
        tooltip.classed("hidden", true)
      })
      .on("mousemove", (event, d) => {
        const code = this.prefectureCode(d.properties)
        const label = prefectures[code]?.label || d.properties.nam_ja
        const [x, y] = pointer(event, this.containerTarget)
        tooltip
          .classed("hidden", false)
          .style("left", `${x + 10}px`)
          .style("top", `${y - 30}px`)
          .text(label)
      })
  }

  showMap() {
    this.loadingTarget.classList.add("hidden")
    this.containerTarget.classList.remove("hidden")
    this.containerTarget.style.position = "relative"
  }

  buildUrl(properties) {
    const code = this.prefectureCode(properties)
    const pref = this.prefecturesValue[code]
    const url = pref?.url || this.urlPatternValue?.replace(":code", code) || "#"
    return this.sanitizeUrl(url)
  }

  sanitizeUrl(url) {
    if (url === "#") return url
    try {
      const parsed = new URL(url, window.location.origin)
      // 同一オリジンの相対URLはパス部分のみ返す（//evil.example 等の正規化攻撃を防止）
      if (parsed.origin === window.location.origin) return parsed.pathname + parsed.search + parsed.hash
      // 外部URLはhttp/httpsのみ許可し、正規化済みのhrefを返す
      if (["http:", "https:"].includes(parsed.protocol)) return parsed.href
    } catch (_e) {
      // invalid URL
    }
    return "#"
  }

  prefectureCode(properties) {
    return String(properties.id || "").padStart(2, "0")
  }
}
